import { Typography } from "@mui/material";
import * as React from "react";
import { ApiBaseUrl } from "../../config";
import { KeyStringValue, KeyValue } from "../../interfaces";

// Parses date received as string (YYYYMMDD) and returns mm/dd/yyy format
export function reportPeriodDateFormat(txt: string) {
  return (
    typeof txt === "string" && txt.replace(/(\d{4})(\d{2})(\d{2})/g, "$2/$3/$1")
  );
}
export const htmlFootnote = (
  text: string | React.ReactElement,
  index: number,
  className?: string,
) => (
  <Typography
    key={index}
    className={className}
    color="primary.main"
    variant="body2"
    style={{
      marginTop: index === 0 ? "1.5rem" : 0,
      paddingBottom: ".75rem",
    }}
  >
    {text}
  </Typography>
);

export const getReportPeriod = (currentDate: Date) => {
  const currentYear = currentDate.getFullYear();
  const quarterTwo = {
    from: new Date(`04/01/${currentYear}`),
    to: new Date(`06/30/${currentYear}`),
  };
  const quarterThree = {
    from: new Date(`07/01/${currentYear}`),
    to: new Date(`09/30/${currentYear}`),
  };
  const quarterFour = {
    from: new Date(`10/01/${currentYear}`),
    to: new Date(`12/31/${currentYear}`),
  };
  if (currentDate >= quarterTwo.from && currentDate <= quarterTwo.to) {
    return `${currentYear}0630`;
  }
  if (currentDate >= quarterThree.from && currentDate <= quarterThree.to) {
    return `${currentYear}0930`;
  }
  if (currentDate >= quarterFour.from && currentDate <= quarterFour.to) {
    return `${currentYear}1231`;
  }
  return `${currentYear}0331`;
};
export function searchTitleLookup(input: string) {
  const lookup: KeyStringValue = {
    ASSET: "Total Assets",
    DEP: "Total Deposits",
    NETINCQ: "Net Income - Qtr",
    NETINC: "Net Income - YTD",
    ROAPTXQ: "Pre-tax Return on Assets - % Qtr",
    ROAPTX: "Pre-tax Return on Assets - % YTD",
  };
  return lookup[input];
}
const apiKeyLookup: any = {
  charterClass: "BKCLASS",
  charteringAgency: "CHRTAGNT",
  primaryRegulator: "REGAGNT",
  geographic: "FDICDBS",
  supervisory: "FDICSUPV",
  fieldOffices: "FLDOFF",
};

export const rangeSplitDateFormat = (establishedRangeVal: string) => {
  const getformattedDateVals = (index: number) =>
    establishedRangeVal.split("/")[index] &&
    establishedRangeVal.split("/")[index].length > 1
      ? establishedRangeVal.split("/")[index]
      : `0${establishedRangeVal.split("/")[index]}`;
  const month = getformattedDateVals(0);
  const day = getformattedDateVals(1);
  const year = getformattedDateVals(2);
  return `${year}${month}${day}`;
};
export function getRegulatoryValues(
  filterValues: KeyValue,
  ignoreAggByKey?: string,
) {
  let FDICInsured = false;
  const queryArray: any[] = [];
  const allArray = ["ALL", "1"];
  Object.keys(filterValues).forEach((key: string) => {
    if (Object.keys(apiKeyLookup).includes(key)) {
      if (!FDICInsured && allArray.includes(filterValues[key])) {
        FDICInsured = true;
      }
      return filterValues[key] &&
        !allArray.includes(filterValues[key]) &&
        key !== ignoreAggByKey
        ? queryArray.push(`${apiKeyLookup[key]}:"${filterValues[key]}"`)
        : queryArray;
    }
    return queryArray;
  });
  const FDICInsuredQuery =
    queryArray.length > 0 || FDICInsured ? ` AND INSFDIC:1 ` : "";

  const regulatoryQuery =
    queryArray.length > 0 ? `AND ${queryArray.join(" AND ")} ` : "";
  return `${FDICInsuredQuery}${regulatoryQuery}`;
}

export function top100Banks(value: string) {
  return value === "top100";
}

export function generateFinancialsApiUrl(
  appState: KeyValue,
  fields?: string[],
  aggBy?: string,
  institutions?: boolean,
  postReq?: boolean,
) {
  const filtersState = appState.filters ? appState.filters : appState;
  const searchState = appState.search ? appState.search : appState;
  const uiState = appState.ui ? appState.ui : appState;

  const isBHCSearch =
    uiState.institutionType && uiState.institutionType === "bhcs";

  const apiFields = fields ? `&fields=${fields.join()}` : "";
  const bankClassExclusions =
    filtersState.commonSearch &&
    filtersState.commonSearch === "MUTUAL:0 AND INSSAVE:1"
      ? ["N", "SM", "NM", "OI", "NC", "NS", "CU"]
      : ["NC", "NS", "CU"];

  const bhcExclusion = isBHCSearch ? ` AND RSSDHCR:>0` : "";
  const stateSearch = filtersState.stalp
    ? ` AND ${isBHCSearch ? "STALPHCR" : "STALP"}:"${filtersState.stalp}"`
    : "";
  const countySearch =
    isBHCSearch && filtersState.bhcCounty
      ? ` AND STCNTY:"${filtersState.bhcCounty}"`
      : filtersState.county
        ? ` AND STCNTY:"${filtersState.county}"`
        : "";
  const zipSearch = filtersState.zip ? ` AND ZIP:"${filtersState.zip}"` : "";

  const isMsaSearch = Boolean(filtersState.msa);

  const msaSearch = isMsaSearch ? ` AND MSA:(${filtersState.msa})` : "";
  const searchType = filtersState.financials;
  const reportPeriod = appState.reportPeriod ?? filtersState?.reportPeriod?.[0];
  const cert =
    filtersState.cert && !filtersState.cert.includes(",")
      ? `CERT:${filtersState.cert} AND `
      : "";
  const bhcRSSD = filtersState.bhcRSSD
    ? `RSSDHCR:${filtersState.bhcRSSD} AND `
    : "";
  const bhcState = filtersState.bhcState
    ? `STALPHCR:"${filtersState.bhcState}" AND `
    : "";
  const multiCertArrayEmpty =
    filtersState.multiCert &&
    filtersState.multiCert.length === 1 &&
    filtersState.multiCert[0] === "";
  const multiCert =
    filtersState.cert || multiCertArrayEmpty
      ? ""
      : filtersState.multiCert && filtersState.multiCert.length > 0
        ? `CERT:(${filtersState.multiCert
            .filter((cert: string) => cert)
            .join(" OR ")}) AND `
        : "";
  const percentageValues = filtersState?.financials?.includes("ROAPTX");
  const minOrMaxAmount = (minOrMax: number) => {
    return minOrMax && !isNaN(minOrMax)
      ? percentageValues
        ? minOrMax
        : minOrMax / 1000
      : "*";
  };
  const range = searchType
    ? `${searchType}:[${minOrMaxAmount(
        filtersState.minimumRange,
      )} TO ${minOrMaxAmount(filtersState.maximumRange)}] AND `
    : "";

  const establishedRange =
    uiState.limitEstablishedDate && uiState.institutionType === "banks"
      ? `ESTYMD:[${rangeSplitDateFormat(
          filtersState.establishedStartRange,
        )} TO ${rangeSplitDateFormat(filtersState.establishedEndRange)}]  AND `
      : "";

  const inactiveRangeOrRisdate = !institutions
    ? uiState.limitInactiveDate && uiState.institutionType === "banks"
      ? `ENDEFYMD:[${rangeSplitDateFormat(
          filtersState.inactiveStartRange,
        )} TO ${rangeSplitDateFormat(filtersState.inactiveEndRange)}] AND `
      : `RISDATE:"${reportPeriod}" AND `
    : "";

  const inactiveMaxValue = uiState.limitInactiveDate
    ? "&max_value=REPDTE&max_value_by=CERT"
    : "";

  let apiSearch = [];
  if (searchState.city && !isBHCSearch) {
    apiSearch.push(`CITY:${searchState.city}`);
  }
  if (searchState.name) {
    apiSearch.push(`NAME:${encodeURIComponent(searchState.name)}`);
  }
  if (searchState.bhcName) {
    apiSearch.push(`BHCNAME:${encodeURIComponent(searchState.bhcName)}`);
  }
  if (searchState.city && isBHCSearch) {
    apiSearch.push(`BHCCITY:${encodeURIComponent(searchState.city)}`);
  }

  const quarterlyProfile =
    filtersState.profile !== "" && filtersState.profile
      ? ` AND INSALL:1 ${
          filtersState.profile !== "1"
            ? `AND QBPRCOML:${filtersState.profile} `
            : ""
        }`
      : "";
  const charterType = filtersState.charterType
    ? `${filtersState.charterType} AND `
    : "";
  const assetConcentration = filtersState.assetConcentration
    ? filtersState.assetConcentration === "INSALL:1"
      ? `${filtersState.assetConcentration} AND `
      : ` SPECGRP:${filtersState.assetConcentration} AND `
    : "";
  const commonSearch =
    filtersState.commonSearch && !top100Banks(filtersState.commonSearch)
      ? `${filtersState.commonSearch} AND `
      : "";

  const filters = `${cert}${bhcRSSD}${bhcState}${multiCert}${charterType}${assetConcentration}${commonSearch}${inactiveRangeOrRisdate}${range}${establishedRange}${`!BKCLASS:${bankClassExclusions.join(
    " AND !BKCLASS:",
  )}`}${bhcExclusion}${stateSearch}${countySearch}${zipSearch}${msaSearch}${quarterlyProfile}${getRegulatoryValues(
    filtersState,
    aggBy,
  )}${inactiveMaxValue}`;
  const base = `${ApiBaseUrl()}/${
    institutions ? "institutions" : "financials"
  }`;

  const queryParams = [
    aggBy && !inactiveMaxValue ? `agg_by=${apiKeyLookup[aggBy]}` : null,
    `filters=${filters}`,
    `search=${apiSearch.join(" AND ")}`,
    apiFields,
  ]
    .filter(Boolean)
    .join("&");

  const url = `${base}?${queryParams}`;
  return postReq
    ? ({
        filters: `${filters}${
          top100Banks(filtersState.commonSearch) ? " AND top100" : ""
        }`,
        search: apiSearch.join(" AND "),
      } as KeyValue)
    : (url
        .replace("filters= AND ", "filters=")
        .replace(/ AND AND /g, " AND ") as string);
}
export const smallBusinessLoanFootnotes = (noAsterisk?: boolean) => {
  return {
    downloadText: `${
      noAsterisk ? "" : "4. "
    }As of 2017, this item is only reported each June and December for banks that match the following: filing FFIEC Call Report 51, with domestic offices only, and with Total Assets less than $5 billion ($1 billion 3/17-6/19). The FFIEC Call Report number is listed on each bank's Demographic report or download. Prior to 2010 it is available on June 30th only. As of 2010 this item is available on a quarterly basis.`,
    text: (
      <>
        {`${
          noAsterisk ? "" : "4. "
        }As of 2017, this item is only reported each June and December for banks that match the following: filing `}
        <a href="https://www7.fdic.gov/sdi/definitions.asp?SystemForm=ID&amp;HelpItem=callform">
          FFIEC Call Report 51
        </a>
        {
          ", with domestic offices only, and with Total Assets less than $5 billion ($1 billion 3/17-6/19). The FFIEC Call Report number is listed on each bank's Demographic report or download. Prior to 2010 it is available on June 30th only. As of 2010 this item is available on a quarterly basis."
        }
      </>
    ),
  };
};

export const offBalanceSheetFootnotes = (noAsterisk?: boolean) => {
  return {
    downloadText: `${
      noAsterisk ? "" : "5. "
    } As of 2017, only the line items "Total Derivatives" and "Interest Rate Contracts" are reported by banks that match the following: are filing a FFIEC Call Report 51, with domestic offices only, and Total Assets of less than $1 billion. In September of 2019 the criteria change to: banks filing a FFIEC Call Report 51, with domestic offices only, and Total Assets of less than $5 billion.`,
    text: (
      <>
        {`${
          noAsterisk ? "" : "5. "
        } As of 2017, only the line items "Total Derivatives" and "Interest Rate Contracts" are reported by banks that match the following: are filing a `}
        <a href="https://www7.fdic.gov/sdi/definitions.asp?SystemForm=ID&amp;HelpItem=callform">
          FFIEC Call Report 51
        </a>
        {
          ", with domestic offices only, and Total Assets of less than $1 billion. In September of 2019 the criteria change to: banks filing a "
        }{" "}
        <a href="https://www7.fdic.gov/sdi/definitions.asp?SystemForm=ID&amp;HelpItem=callform">
          FFIEC Call Report 51
        </a>
        {
          ", with domestic offices only, and Total Assets of less than $5 billion."
        }
      </>
    ),
  };
};

export const labelFootnoteLookup = (reportType: string, download?: boolean) => {
  const footnotes: KeyValue = {
    "performance-and-condition-ratios": [
      {
        text: "1. For institutions that have adopted CECL methodology (ASU 2016-13), this item represents provisions for all credit losses on a consolidated basis. For institutions that have not adopted CECL methodology (ASU 2016-13), this item represents the provision for loan and lease losses.",
      },

      {
        text: "2. For institutions that have adopted CECL methodology (ASU 2016-13) this item represents the allowance for credit losses on loans and leases held for investment and allocated transfer risk.",
      },

      {
        text: "3. As of March 2020, not available for institutions that have elected the Community Bank Leverage Ratio (CBLR) framework.",
      },
    ],
    "assets-liabilities-and-capital": [
      {
        text: "1. Most detail data for government obligations is not available for TFR Reporters and therefore subitems may not total properly. The last TFR reports were filed December 2011.",
      },
      {
        text: "2. For institutions that have adopted CECL Methodology (ASU 2016-13), securities are reported net of allowances for credit losses.",
      },
      {
        text: "3. Call 51 filers do not report amounts conveyed to others by reporting bank. Most of the items are not available for TFR Reporters, before  2012.",
      },
      smallBusinessLoanFootnotes(),
      offBalanceSheetFootnotes(),
      {
        text: "6. Note 1: This report became publicly available as of March 31, 2009 on a year-to-date income basis. Note 2: Prior to 2018, institutions with total fiduciary assets greater than $100 million but less than or equal to $250 million with fiduciary and related income greater than 10% of total income, based on the previous December Call Report, reported in December only. Note 3: As of June 2018, only institutions with total fiduciary assets greater than $250 million or with fiduciary and related income greater than 10% total income, based on the previous December Call Report, will reported this item quarterly. All other institutions report in December. Note 4: Beginning September 2019 the reporting frequency for FFIEC Call Report 51 filers changed based on total fiduciary asset thresholds, which are based on the previous December Call Report. They file quarterly if fiduciary assets greater than $1 billion or fiduciary income is over 10% of revenue. They file in June and December if fiduciary assets are $250 million to $1 billion and do not meet the fiduciary income test for quarterly reporting. Otherwise they file yearly in December. The FFIEC Call Report number is listed on each bank's Demographic report or download.",
      },
      {
        text: "7. Calculated by taking Estimated Insured Deposits as a percentage of Deposit Liabilities after Exclusions. Prior to 2nd quarter 2023, this calculation is before exclusions.",
      },
    ],
    "summary-table": [
      {
        text: "1. For institutions that have adopted CECL methodology (ASU 2016-13), this item represents provisions for all credit losses on a consolidated basis. For institutions that have not adopted CECL methodology (ASU 2016-13), this item represents the provision for loan and lease losses.",
      },

      {
        text: "2. For institutions that have adopted CECL methodology (ASU 2016-13) this item represents the allowance for credit losses on loans and leases held for investment and allocated transfer risk.",
      },

      {
        text: "3. As of March 2020, not available for institutions that have elected the Community Bank Leverage Ratio (CBLR) framework.",
      },
    ],
  };

  const footNoteReturn = footnotes[reportType] || [{ text: "" }];
  return footNoteReturn.map((footnotes: any, index: number) => {
    return download
      ? footnotes.downloadText
        ? footnotes.downloadText
        : footnotes.text
      : htmlFootnote(footnotes.text, index);
  });
};
